import { Fragment } from "react";
import { NavLink } from "react-router-dom";
import { Box, ButtonBase, Icon, styled } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableViewIcon from '@mui/icons-material/TableView';
import useSettings from "app/hooks/useSettings";
import { Paragraph, Span } from "../Typography";
import MatxVerticalNavExpansionPanel from "./MatxVerticalNavExpansionPanel";
import PrintIcon from '@mui/icons-material/Print';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import MessageIcon from '@mui/icons-material/Message';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCardIcon from '@mui/icons-material/AddCard';
import PageviewIcon from '@mui/icons-material/Pageview';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import WarningIcon from '@mui/icons-material/Warning';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import BadgeIcon from '@mui/icons-material/Badge';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArticleIcon from '@mui/icons-material/Article';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import { School } from '@mui/icons-material';
// STYLED COMPONENTS
const ListLabel = styled(Paragraph)(({ theme, mode }) => ({
  fontSize: "12px",
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "10px",
  textTransform: "uppercase",
  display: mode === "compact" && "none",
  color: theme.palette.text.secondary
}));

const ExtAndIntCommon = {
  display: "flex",
  overflow: "hidden",
  borderRadius: "4px",
  height: 44,
  whiteSpace: "pre",
  marginBottom: "8px",
  textDecoration: "none",
  justifyContent: "space-between",
  transition: "all 150ms ease-in",
  "&:hover": { background: "rgba(255, 255, 255, 0.08)" },
  "&.compactNavItem": {
    overflow: "hidden",
    justifyContent: "center !important"
  },
  "& .icon": {
    fontSize: "18px",
    paddingLeft: "16px",
    paddingRight: "16px",
    verticalAlign: "middle"
  }
};

const ExternalLink = styled("a")(({ theme }) => ({
  ...ExtAndIntCommon,
  color: theme.palette.text.primary
}));

const InternalLink = styled(Box)(({ theme }) => ({
  "& a": {
    ...ExtAndIntCommon,
    color: theme.palette.text.primary
  },
  "& .navItemActive": {
    backgroundColor: "rgba(255, 255, 255, 0.16)"
  }
}));

const StyledText = styled(Span)(({ mode }) => ({
  fontSize: "0.875rem",
  paddingLeft: "0.8rem",
  display: mode === "compact" && "none"
}));

const AddIconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginLeft: "24px",
  marginRight: "8px",
}));

const BadgeValue = styled("div")(() => ({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "300px"
}));

export default function MatxVerticalNav({ items, onAddClick }) {
  const { settings } = useSettings();
  const { mode } = settings.layout1Settings.leftSidebar;

  const renderLevels = (data) => {
    return data.map((item, index) => {
      if (item.type === "label")
        return (
          <ListLabel key={index} mode={mode} className="sidenavHoverShow">
            {item.label}
          </ListLabel>
        );

      if (item.children) {
        return (
          <MatxVerticalNavExpansionPanel mode={mode} item={item} key={index}>
            {renderLevels(item.children)}
          </MatxVerticalNavExpansionPanel>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            className={`${mode === "compact" && "compactNavItem"}`}
            rel="noopener noreferrer"
            target="_blank">
            <ButtonBase key={item.name} name="child" sx={{ width: "100%" }}>
              {(() => {
                if (item.icon) {
                  return <Icon className="icon">{item.icon}</Icon>;
                } else {
                  return <span className="item-icon icon-text">{item.iconText}</span>;
                }
              })()}
              <StyledText mode={mode} className="sidenavHoverShow">
                {item.name}
              </StyledText>
              <Box mx="auto"></Box>
              {item.badge && <BadgeValue>{item.badge.value}</BadgeValue>}
            </ButtonBase>
          </ExternalLink>
        );
      } else {
        return (
          <InternalLink key={index}>
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                isActive
                  ? `navItemActive ${mode === "compact" && "compactNavItem"}`
                  : `${mode === "compact" && "compactNavItem"}`
              }>
              <ButtonBase key={item.name} name="child" sx={{ width: "100%" }}>
                {item?.icon ? (
                  <Icon className="icon" sx={{ width: 36 }}>
                    {item.icon}
                  </Icon>
                ) : item.name === "Add Student" ? (
                  <AddIconWrapper>
                    <AddIcon sx={{ fontSize: "18px" }} />
                  </AddIconWrapper>
                ) : item.name === "Add Teacher" ? (
                  <AddIconWrapper>
                    <AddIcon sx={{ fontSize: "18px" }} />
                  </AddIconWrapper>
                ) : item.name === "Students Details" ? (
                  <AddIconWrapper>
                    <VisibilityIcon sx={{ fontSize: "18px" }} />
                  </AddIconWrapper>
                ) : item.name === "Exam Group" ? (
                  <AddIconWrapper>
                    <TableViewIcon sx={{ fontSize: "18px" }} />
                  </AddIconWrapper>
                ) : item.name === "Print Marksheet" ? (
                  <AddIconWrapper>
                    <PrintIcon sx={{ fontSize: "18px" }} />
                  </AddIconWrapper>
                )
                  : item.name === "Allocate Teacher" ? (
                    <AddIconWrapper>
                      <ChevronRightIcon sx={{ fontSize: "18px" }} />
                    </AddIconWrapper>
                  )
                    : item.name === "Add Subject" ? (
                      <AddIconWrapper>
                        <AddIcon sx={{ fontSize: "18px" }} />
                      </AddIconWrapper>
                    )
                      : item.name === "Add class / Section" ? (
                        <AddIconWrapper>
                          <AddIcon sx={{ fontSize: "18px" }} />
                        </AddIconWrapper>
                      )
                        : item.name === "View Teacher" ? (
                          <AddIconWrapper>
                            <VisibilityIcon sx={{ fontSize: "18px" }} />
                          </AddIconWrapper>
                        )
                          : item.name === "View Profile" ? (
                            <AddIconWrapper>
                              <AccountBoxIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Marks" ? (
                            <AddIconWrapper>
                              <VisibilityIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Message Student" ? (
                            <AddIconWrapper>
                              <MessageIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Message Teacher" ? (
                            <AddIconWrapper>
                              <MessageIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Message" ? (
                            <AddIconWrapper>
                              <MessageIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Promote Students" ? (
                            <AddIconWrapper>
                              <HowToRegIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Add Book" ? (
                            <AddIconWrapper>
                              <AddIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Delete Book" ? (
                            <AddIconWrapper>
                              <DeleteForeverRoundedIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Issue Book" ? (
                            <AddIconWrapper>
                              <LibraryBooksIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Return Book" ? (
                            <AddIconWrapper>
                              <AssignmentReturnOutlinedIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Pay Fine" ? (
                            <AddIconWrapper>
                              <PaymentIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Books" ? (
                            <AddIconWrapper>
                              <LibraryBooksIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Movement Log" ? (
                            <AddIconWrapper>
                              <TrackChangesIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Assignment" ? (
                            <AddIconWrapper>
                              <AssignmentIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Assignments" ? (
                            <AddIconWrapper>
                              <AssignmentIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Assignment" ? (
                            <AddIconWrapper>
                              <AssignmentIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>

                          ) : item.name === "Download Details" ? (
                            <AddIconWrapper>
                              <FileDownloadIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Assign Fees" ? (
                            <AddIconWrapper>
                              <AddCardIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Assign Fees" ? (
                            <AddIconWrapper>
                              <PageviewIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Record Payment" ? (
                            <AddIconWrapper>
                              <FiberManualRecordIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Fees unpaid status" ? (
                            <AddIconWrapper>
                              <WarningIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "Add Comment" ? (
                            <AddIconWrapper>
                              <AddCommentIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>
                          ) : item.name === "View Comment" ? (
                            <AddIconWrapper>
                              <MessageIcon sx={{ fontSize: "18px" }} />
                            </AddIconWrapper>) : item.name === "Generate TC" ? (
                              <AddIconWrapper>
                                <CallToActionIcon sx={{ fontSize: "18px" }} />
                              </AddIconWrapper>) : item.name === "Card" ? (
                                <AddIconWrapper>
                                  <BadgeIcon sx={{ fontSize: "18px" }} />
                                </AddIconWrapper>)
                            : item.name === "Log Sheet" ? (
                              <AddIconWrapper>
                                <ListAltIcon sx={{ fontSize: "18px" }} />
                              </AddIconWrapper>)
                              : item.name === "Add User" ? (
                                <AddIconWrapper>
                                  <AddIcon sx={{ fontSize: "18px" }} />
                                </AddIconWrapper>
                              )
                                : item.name === "Add Session" ? (
                                  <AddIconWrapper>
                                    <AddIcon sx={{ fontSize: "18px" }} />
                                  </AddIconWrapper>
                                )
                                  : item.name === "Remark & Attendance" ? (
                                    <AddIconWrapper>
                                      <ListAltIcon sx={{ fontSize: "18px" }} />
                                    </AddIconWrapper>)
                                    : item.name === "Bulk Photo Upload" ? (
                                      <AddIconWrapper>
                                        <AddPhotoAlternateIcon sx={{ fontSize: "18px" }} />
                                      </AddIconWrapper>
                                    )
                                      : item.name === "Pre Board" ? (
                                        <AddIconWrapper>
                                          <ArticleIcon sx={{ fontSize: "18px" }} />
                                        </AddIconWrapper>)
                                        : item.name === "Certificate" ? (
                                          <AddIconWrapper>
                                            <WorkspacePremiumIcon sx={{ fontSize: "18px" }} />
                                          </AddIconWrapper>)
                                          : item.name === "TC Records" ? (
                                            <AddIconWrapper>
                                              <BadgeIcon sx={{ fontSize: "18px" }} />
                                            </AddIconWrapper>)
                                            : item.name === "Teacher Attendance" ? (
                                              <AddIconWrapper>
                                                <EventAvailableIcon sx={{ fontSize: "18px" }} />
                                              </AddIconWrapper>)
                                              : item.name === "Birthday Info" ? (
                                                <AddIconWrapper>
                                                  <School sx={{ fontSize: "18px" }} />
                                                </AddIconWrapper>
                                              )
                                                : null}
                <StyledText mode={mode} className="sidenavHoverShow">
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue className="sidenavHoverShow">{item.badge.value}</BadgeValue>
                )}
              </ButtonBase>
            </NavLink>
          </InternalLink>
        );
      }
    });
  };

  return (
    <div className="navigation">
      {renderLevels(items)}
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 2,
          cursor: "pointer",
          borderRadius: 1,
          bgcolor: "primary.main",
          color: "white",
          p: 1,
        }}
        onClick={onAddClick}
      >
         <AddIcon /> 
         <StyledText mode={mode}>Add New</StyledText> 
      </Box> */}
    </div>
  );
}